import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet as RouterOutlet, NavLink as RouterNavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlatform } from '../../context/PlatformContext';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import {
  FiGrid, FiUsers, FiSettings, FiBriefcase, FiAward, FiBell, FiSearch,
  FiLayers, FiMail, FiLayout, FiShield, FiX, FiCheck,
  FiBarChart2, FiChevronRight, FiMenu, FiExternalLink, FiFileText, FiLogOut
} from 'react-icons/fi';

// ─── Sidebar Navigation Items ─────────────────────────────────────
const SIDEBAR_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { path: '/dashboard', icon: FiGrid, label: 'Dashboard' },
    ],
  },
  {
    label: 'Operations Workflow',
    items: [
      { path: '/templates', icon: FiLayout, label: 'Templates' },
      { path: '/candidates', icon: FiUsers, label: 'Offer Letters' },
      { path: '/email', icon: FiMail, label: 'Email Center' },
    ],
  },
  {
    label: 'Management & Trust',
    items: [
      { path: '/programs', icon: FiLayers, label: 'All Programs' },
      { path: '/verification', icon: FiShield, label: 'Verification' },
      { path: '/analytics', icon: FiBarChart2, label: 'Analytics' },
    ],
  },
  {
    label: 'System',
    items: [
      { path: '/settings', icon: FiSettings, label: 'Settings' },
    ],
  },
];

// ─── Breadcrumb Label Map ────────────────────────────────────
const ROUTE_LABELS = {
  '/dashboard': 'Dashboard',
  '/programs': 'Internship Programs',
  '/candidates': 'Offer Letters',
  '/templates': 'Templates',
  '/email': 'Email Center',
  '/verification': 'Verification',
  '/analytics': 'Global Analytics',
  '/settings': 'Settings',
};

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, setMode, notifications, candidates, emailHistory, programs } = usePlatform();
  const { logout, session } = useAuth();

  const [showNotifs, setShowNotifs] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  const failedEmails = emailHistory.filter(e => e.status === 'failed').length;
  const unreadCount = (notifications || []).filter(n => !n.read).length;
  const totalNotifCount = unreadCount + failedEmails;

  // Global Search by Email, Name, or Intern ID
  const handleSearch = useCallback((q) => {
    setGlobalSearch(q);
    if (!q.trim()) {
      setSearchResults([]);
      return;
    }
    const ql = q.toLowerCase().trim();

    // 1. Search Candidates by Name, Email, Intern ID (generatedId/verificationId), Role, College
    const candidateResults = (candidates || [])
      .filter(c =>
        c.name?.toLowerCase().includes(ql) ||
        c.email?.toLowerCase().includes(ql) ||
        c.generatedId?.toLowerCase().includes(ql) ||
        c.verificationId?.toLowerCase().includes(ql) ||
        c.college?.toLowerCase().includes(ql) ||
        c.role?.toLowerCase().includes(ql)
      )
      .slice(0, 6)
      .map(c => ({
        id: c.id,
        label: c.name || 'Candidate',
        sub: c.generatedId || c.verificationId || c.email,
        email: c.email,
        internId: c.generatedId || c.verificationId || 'ID Pending',
        docStatus: c.docStatus,
        path: '/candidates',
        type: 'Candidate'
      }));

    // 2. Search Programs
    const programResults = (programs || [])
      .filter(p =>
        p.name?.toLowerCase().includes(ql) ||
        p.domain?.toLowerCase().includes(ql) ||
        p.code?.toLowerCase().includes(ql)
      )
      .slice(0, 3)
      .map(p => ({
        id: p.id,
        label: p.name,
        sub: `${p.code || ''} · ${p.domain || ''}`,
        path: `/programs/${p.id}`,
        type: 'Program'
      }));

    setSearchResults([...candidateResults, ...programResults]);
    setShowSearchDropdown(true);
  }, [candidates, programs]);

  // Click outside to close search dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut: Ctrl+K or / to focus top search bar
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        setShowSearchDropdown(true);
      }
      if (e.key === 'Escape') {
        setShowSearchDropdown(false);
        setShowNotifs(false);
        searchInputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowSearchDropdown(false);
  }, [location.pathname]);

  const selectSearchResult = (item) => {
    navigate(item.path);
    setShowSearchDropdown(false);
    setGlobalSearch('');
  };

  // Current page breadcrumb
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const currentLabel = ROUTE_LABELS[location.pathname] || pathSegments[pathSegments.length - 1] || 'Dashboard';
  const isDetailPage = pathSegments.length > 1 && pathSegments[0] === 'programs';

  // Sidebar content
  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className={`flex items-center ${sidebarCollapsed ? 'justify-center px-3' : 'px-5'} h-16 border-b border-surface-200 flex-shrink-0 bg-white`}>
        <img
          src="/logo.png"
          alt="Innovation Hacks Logo"
          className="w-10 h-10 object-contain flex-shrink-0"
        />
        {!sidebarCollapsed && (
          <div className="ml-3 min-w-0">
            <span className="text-base font-black text-slate-950 tracking-tight block">Innovation Hacks</span>
            <span className="text-[10px] text-brand-600 font-extrabold tracking-wide uppercase">Build · Innovate · Impact</span>
          </div>
        )}
      </div>

      {/* Navigation Sections */}
      <nav className={`flex-1 overflow-y-auto py-4 ${sidebarCollapsed ? 'px-2' : 'px-3'} space-y-6`}>
        {SIDEBAR_SECTIONS.map((section) => (
          <div key={section.label}>
            {!sidebarCollapsed && (
              <div className="text-[11px] font-black text-slate-900 uppercase tracking-wider px-3 mb-2">
                {section.label}
              </div>
            )}
            <div className="space-y-1">
              {section.items.map((item) => (
                <RouterNavLink
                  key={item.path}
                  to={item.path}
                  title={sidebarCollapsed ? item.label : undefined}
                  className={({ isActive }) => `
                    relative flex items-center gap-3 ${sidebarCollapsed ? 'justify-center px-3' : 'px-3'} py-2.5 rounded-xl
                    transition-all duration-150 group text-sm font-bold
                    ${isActive
                      ? 'bg-brand-50 text-brand-700 font-extrabold shadow-sm'
                      : 'text-slate-800 hover:bg-surface-100 hover:text-slate-950'}
                  `}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <motion.div
                          layoutId="sidebar-active"
                          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-brand-600 rounded-r-full"
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        />
                      )}
                      <SafeIcon
                        icon={item.icon}
                        className={`w-[19px] h-[19px] flex-shrink-0 transition-colors duration-150
                          ${isActive ? 'text-brand-600' : 'text-slate-700 group-hover:text-slate-950'}`}
                      />
                      {!sidebarCollapsed && (
                        <span className="truncate text-[13px]">{item.label}</span>
                      )}
                      {!sidebarCollapsed && item.path === '/email' && failedEmails > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-black rounded-md flex items-center justify-center flex-shrink-0">
                          {failedEmails}
                        </span>
                      )}
                    </>
                  )}
                </RouterNavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer / Logout */}
      <div className="p-4 border-t border-surface-200 flex-shrink-0 bg-white">
        {!sidebarCollapsed ? (
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-2.5 bg-surface-100 border border-surface-200 rounded-xl">
              <img
                src="/logo.png"
                alt="Innovation Hacks"
                className="w-8 h-8 object-contain rounded-lg bg-white p-0.5 border border-surface-200 shadow-xs flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-bold text-slate-950 truncate">
                  {session?.user?.email?.split('@')[0] || 'Admin'}
                </div>
                <div className="text-[10px] text-brand-700 font-bold truncate">{session?.user?.email}</div>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-slate-500 hover:text-red-600 hover:bg-red-50 text-[12px] font-bold transition-all"
            >
              <FiLogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        ) : (
          <button
            onClick={logout}
            title="Sign Out"
            className="w-full flex items-center justify-center p-2 rounded-xl text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all"
          >
            <FiLogOut className="w-5 h-5" />
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-surface-50 overflow-hidden font-sans text-slate-900">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex ${sidebarCollapsed ? 'w-[68px]' : 'w-[260px]'} bg-white flex-col z-20 transition-all duration-300 ease-out border-r border-surface-200 flex-shrink-0`}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 250 }}
              className="fixed left-0 top-0 bottom-0 w-[260px] bg-white z-50 flex flex-col shadow-2xl lg:hidden"
            >
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        {/* Top Header Navbar with prominent search bar */}
        <header className="h-16 bg-white border-b border-surface-200 flex items-center justify-between px-4 lg:px-8 z-30 sticky top-0 flex-shrink-0 gap-4">
          
          {/* Left: Mobile Toggle & Breadcrumbs */}
          <div className="flex items-center gap-3 min-w-fit">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 text-slate-700 hover:text-slate-950 hover:bg-surface-100 rounded-xl transition-all"
            >
              <FiMenu className="w-5 h-5" />
            </button>

            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex p-2 text-slate-600 hover:text-slate-950 hover:bg-surface-100 rounded-xl transition-all"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <FiMenu className="w-4 h-4" />
            </button>

            <div className="hidden sm:flex items-center gap-1.5 text-sm">
              <span className="text-slate-500 font-bold">Innovation Hacks</span>
              <FiChevronRight className="w-3.5 h-3.5 text-slate-400 font-bold" />
              {isDetailPage ? (
                <>
                  <RouterNavLink to="/programs" className="text-slate-600 hover:text-brand-600 font-bold transition-colors">
                    Programs
                  </RouterNavLink>
                  <FiChevronRight className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-950 font-black">Program Detail</span>
                </>
              ) : (
                <span className="text-slate-950 font-black">{currentLabel}</span>
              )}
            </div>
          </div>

          {/* Center: TOP SEARCH BAR (Search by Email, Name, Intern ID) */}
          <div className="flex-1 max-w-xl relative" ref={searchContainerRef}>
            <div className="relative flex items-center">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={globalSearch}
                onFocus={() => {
                  if (globalSearch.trim()) setShowSearchDropdown(true);
                }}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by Email, Name, or Intern ID..."
                className="w-full h-10 pl-10 pr-20 bg-surface-50 border border-surface-200 rounded-xl text-sm font-semibold text-slate-950 placeholder:text-slate-500 focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 focus:bg-white transition-all shadow-sm"
              />
              <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {globalSearch ? (
                  <button
                    onClick={() => {
                      setGlobalSearch('');
                      setSearchResults([]);
                      setShowSearchDropdown(false);
                    }}
                    className="p-1 text-slate-400 hover:text-slate-700 rounded-md"
                  >
                    <FiX className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-white border border-surface-200 rounded text-[10px] font-bold text-slate-500 shadow-sm">
                    Ctrl K
                  </kbd>
                )}
              </div>
            </div>

            {/* Instant Search Dropdown */}
            <AnimatePresence>
              {showSearchDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 right-0 top-12 bg-white rounded-2xl border border-surface-200 shadow-elevated z-50 overflow-hidden max-h-96 overflow-y-auto"
                >
                  {searchResults.length > 0 ? (
                    <div className="p-2 space-y-1">
                      <div className="px-3 py-1.5 text-[10px] font-black text-slate-500 uppercase tracking-wider">
                        Matching Results ({searchResults.length})
                      </div>
                      {searchResults.map((item, idx) => (
                        <div
                          key={idx}
                          onClick={() => selectSearchResult(item)}
                          className="flex items-center justify-between p-3 hover:bg-brand-50/50 rounded-xl cursor-pointer transition-colors border border-transparent hover:border-brand-100 group"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                              item.type === 'Program'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-brand-100 text-brand-700'
                            }`}>
                              {item.type === 'Program' ? <FiLayers className="w-4 h-4" /> : <FiUsers className="w-4 h-4" />}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-bold text-slate-950 group-hover:text-brand-700 truncate">
                                {item.label}
                              </div>
                              <div className="text-xs text-slate-600 font-medium truncate flex items-center gap-2">
                                <span>{item.email || item.sub}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                            {item.internId && (
                              <code className="text-xs font-bold text-brand-700 bg-brand-50 border border-brand-100 px-2 py-0.5 rounded-lg">
                                {item.internId}
                              </code>
                            )}
                            <FiChevronRight className="w-4 h-4 text-slate-400 group-hover:text-brand-600" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : globalSearch.trim() ? (
                    <div className="p-6 text-center text-sm font-semibold text-slate-600">
                      No candidate, email, or intern ID matching "{globalSearch}"
                    </div>
                  ) : null}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right: Actions & User Info */}
          <div className="flex items-center gap-3">
            {/* Mode Switcher */}
            <div className="hidden md:flex items-center bg-surface-100 rounded-xl p-1 border border-surface-200">
              <button
                onClick={() => setMode('internship')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  mode === 'internship'
                    ? 'bg-white text-brand-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <FiBriefcase className="w-3.5 h-3.5" /> Internship
              </button>
              <button
                onClick={() => setMode('certificate')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  mode === 'certificate'
                    ? 'bg-white text-brand-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <FiAward className="w-3.5 h-3.5" /> Certificate
              </button>
            </div>

            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifs(!showNotifs)}
                className="relative w-10 h-10 flex items-center justify-center rounded-xl bg-surface-50 border border-surface-200 text-slate-700 hover:text-slate-950 hover:border-brand-300 transition-all shadow-sm"
              >
                <FiBell className="w-4 h-4" />
                {totalNotifCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-[10px] font-black rounded-full flex items-center justify-center shadow">
                    {totalNotifCount}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {showNotifs && (
                  <motion.div
                    initial={{ opacity: 0, y: 4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-12 w-80 bg-white rounded-2xl border border-surface-200 shadow-elevated overflow-hidden z-50"
                  >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200">
                      <span className="font-black text-slate-950 text-sm">Notifications</span>
                      <button onClick={() => setShowNotifs(false)} className="text-slate-500 hover:text-slate-900 p-1 rounded-lg hover:bg-surface-100">
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                    {failedEmails > 0 && (
                      <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border-b border-red-100">
                        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                          <FiMail className="text-red-600 w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-xs font-black text-red-900">{failedEmails} email(s) failed</div>
                          <div className="text-[11px] text-red-700 font-semibold">Open Email Center to retry</div>
                        </div>
                      </div>
                    )}
                    {(notifications || []).slice(0, 4).map(n => (
                      <div key={n.id} className={`flex items-start gap-3 px-4 py-3 border-b border-surface-100 cursor-pointer hover:bg-surface-50 transition-all ${!n.read ? 'bg-brand-50/20' : ''}`}>
                        <div className="w-2 h-2 rounded-full bg-brand-600 mt-1.5 flex-shrink-0" />
                        <div>
                          <div className="text-xs font-bold text-slate-900">{n.message}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5 font-medium">{new Date(n.createdAt).toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                    {totalNotifCount === 0 && (
                      <div className="text-center py-8 text-slate-500 text-sm font-semibold">All caught up! 🎉</div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Innovation Hacks Logo Avatar */}
            <div className="w-10 h-10 rounded-xl bg-white border border-surface-200 p-1 flex items-center justify-center shadow-xs cursor-pointer hover:shadow-md hover:border-brand-300 transition-all">
              <img
                src="/logo.png"
                alt="Innovation Hacks"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div
          className="flex-1 overflow-y-auto relative"
          onClick={() => {
            if (showNotifs) setShowNotifs(false);
            if (showSearchDropdown) setShowSearchDropdown(false);
          }}
        >
          <div className="relative z-10 px-6 lg:px-10 py-8 max-w-[1440px] mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <RouterOutlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
}